import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined"
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { useEffect, useState } from "react"

import {
  MULTIPART_HEADERS,
  useAuthorizedAxiosClient,
} from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import ApplySignatureToAllTemplatesDialog from "../components/ApplySignatureToAllTemplatesDialog"
import EmailSignatureEditor, {
  useEmailSignatureEditorState,
} from "../components/EmailSignatureEditor"
import SignatureImageUpload from "../components/SignatureImageUpload"
import {
  EMAIL_SIGNATURE_API_URL,
  buildInitialEditorHtml,
  savedSignatureHasContent,
  wrapSignaturePreview,
} from "../components/emailSignatureUtils"
import { recruitProfileSectionCardSx } from "./recruitProfileSectionStyles"

function SignaturePreviewPanel({ previewHtml, hasSavedSignature }) {
  const theme = useTheme()
  const accent = theme.palette.colors?.brand500 ?? theme.palette.primary.main
  const softBg =
    theme.palette.mode === "dark"
      ? "rgba(137, 47, 206, 0.08)"
      : "rgba(137, 47, 206, 0.04)"

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: `linear-gradient(135deg, ${softBg} 0%, transparent 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <MailOutlineOutlinedIcon sx={{ color: accent, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Live preview
          </Typography>
        </Row>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          How your signature appears at the end of outgoing emails
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: 2,
          minHeight: { xs: 180, md: 280 },
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            borderRadius: 1.5,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: theme.palette.grey[50],
            ...(theme.palette.mode === "dark" && {
              bgcolor: "rgba(255,255,255,0.03)",
            }),
            p: 1.5,
            flex: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Sample message body…
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Hi Alex, Thanks for your time today. Looking forward to our next
            conversation.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          {previewHtml ? (
            <Typography
              component="div"
              variant="body2"
              color="text.primary"
              sx={{
                "& *": { marginBlock: 0 },
                "& img": { maxWidth: "100%", height: "auto" },
                "& a": { color: accent },
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <Column
              sx={{
                alignItems: "center",
                justifyContent: "center",
                py: 3,
                gap: 1,
                textAlign: "center",
              }}
            >
              <DrawOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
              <Typography variant="body2" color="text.secondary">
                {hasSavedSignature
                  ? "Start editing to see your signature preview"
                  : "Create your signature to see a live preview here"}
              </Typography>
            </Column>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default function EmailSignatureSection({ onStatusMessage }) {
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [previewHtml, setPreviewHtml] = useState("")
  const [hasSavedSignature, setHasSavedSignature] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [applyDialogOpen, setApplyDialogOpen] = useState(false)
  const [loadedHtml, setLoadedHtml] = useState(null)
  const {
    editorReady,
    setEditorReady,
    editorRef,
    updateEditorContents,
    getEditorHtml,
    insertImageIntoEditor,
    signatureHtmlHasContent: editorHasContent,
  } = useEmailSignatureEditorState(true)
  const axios = useAuthorizedAxiosClient()

  function updatePreviewFromEditor() {
    setPreviewHtml(wrapSignaturePreview(getEditorHtml()))
    setIsDirty(true)
  }

  function handleSignatureImageReady(imageDataUrl) {
    insertImageIntoEditor(imageDataUrl)
    updatePreviewFromEditor()
  }

  function applyLoadedHtml(html, saved) {
    setLoadedHtml(html)
    setHasSavedSignature(saved)
    setPreviewHtml(wrapSignaturePreview(html))
    setIsDirty(false)
    if (editorReady) {
      updateEditorContents(html)
    }
  }

  function loadSignature() {
    setIsLoading(true)
    return axios
      .get(EMAIL_SIGNATURE_API_URL)
      .then(({ data }) => {
        const initialHtml = buildInitialEditorHtml(
          data.email_signature_text,
          data.email_signature_image
        )
        applyLoadedHtml(initialHtml, savedSignatureHasContent(data))
      })
      .finally(() => setIsLoading(false))
  }

  function handleSaveButtonClicked() {
    const signatureHtml = getEditorHtml()
    const formData = new FormData()
    formData.append("email_signature_text", signatureHtml)
    formData.append("clear_signature_image", "true")

    const hasContent = editorHasContent(signatureHtml)

    setIsSaving(true)
    axios
      .patch(EMAIL_SIGNATURE_API_URL, formData, MULTIPART_HEADERS)
      .then(() => {
        setHasSavedSignature(hasContent)
        setIsDirty(false)
        onStatusMessage?.("Email signature saved.")
        if (hasContent) {
          setApplyDialogOpen(true)
        }
      })
      .finally(() => setIsSaving(false))
  }

  function handleApplyDialogClosed(applied, updatedCount = 0) {
    setApplyDialogOpen(false)
    if (applied) {
      if (updatedCount > 0) {
        onStatusMessage?.(
          `Email signature enabled on ${updatedCount} template${
            updatedCount === 1 ? "" : "s"
          }.`
        )
      } else {
        onStatusMessage?.(
          "No email templates found. Create a template first, then enable signature per template."
        )
      }
    }
  }

  useEffect(() => {
    loadSignature()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!editorReady || loadedHtml === null) {
      return
    }
    updateEditorContents(loadedHtml)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, loadedHtml])

  const accent = theme.palette.colors?.brand500 ?? theme.palette.primary.main

  return (
    <>
      <Card variant="outlined" sx={recruitProfileSectionCardSx(theme)}>
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(137, 47, 206, 0.1)",
                color: accent,
              }}
            >
              <AutoAwesomeOutlinedIcon />
            </Box>
          }
          title="Email signature"
          subheader="Add a professional closing to every template. Format text in the editor and upload images separately."
          titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
          subheaderTypographyProps={{
            variant: "body2",
            sx: { mt: 0.5, maxWidth: 640 },
          }}
          action={
            <Chip
              size="small"
              label={hasSavedSignature ? "Configured" : "Not set up"}
              sx={{
                fontWeight: 600,
                mt: 0.5,
                ...(hasSavedSignature
                  ? {
                      bgcolor: "success.main",
                      color: "success.contrastText",
                    }
                  : {
                      bgcolor: "action.selected",
                      color: "text.secondary",
                    }),
              }}
            />
          }
          sx={{
            alignItems: "flex-start",
            pb: 1,
            "& .MuiCardHeader-action": { m: 0 },
          }}
        />
        <CardContent sx={{ pt: 0, px: { xs: 1.5, sm: 2 }, pb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
              gap: 2.5,
              alignItems: "stretch",
            }}
          >
            <Column sx={{ gap: 1.5, minWidth: 0 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Text editor
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Format your name, title, and contact details. Use the image
                  upload section below for logos or photos.
                </Typography>
              </Box>
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                  opacity: isLoading ? 0.65 : 1,
                  transition: "opacity 0.2s ease",
                  "& .tox-tinymce": {
                    border: "none !important",
                  },
                }}
              >
                <EmailSignatureEditor
                  editorRef={editorRef}
                  setEditorReady={setEditorReady}
                  disabled={isLoading}
                  onEditorChange={updatePreviewFromEditor}
                />
              </Box>
              <SignatureImageUpload
                disabled={isLoading || !editorReady}
                onImageReady={handleSignatureImageReady}
              />
              <Row
                sx={{
                  gap: 1.5,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={isLoading || !editorReady}
                  onClick={() => loadSignature()}
                  sx={{ flex: { xs: 1, sm: "none" } }}
                >
                  Reset changes
                </Button>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={isSaving}
                  disabled={isLoading || !editorReady || !isDirty}
                  onClick={handleSaveButtonClicked}
                  sx={{ flex: { xs: 1, sm: "none", minWidth: 160 } }}
                >
                  Save signature
                </LoadingButton>
              </Row>
            </Column>

            <Column sx={{ minWidth: 0 }}>
              {isLoading ? (
                <Skeleton variant="rounded" height={320} />
              ) : (
                <SignaturePreviewPanel
                  previewHtml={previewHtml}
                  hasSavedSignature={hasSavedSignature}
                />
              )}
            </Column>
          </Box>
        </CardContent>
      </Card>

      <ApplySignatureToAllTemplatesDialog
        open={applyDialogOpen}
        onClose={handleApplyDialogClosed}
        onError={(message) => onStatusMessage?.(message)}
      />
    </>
  )
}
